import { notifyPermissionDenied } from './events';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const PERMISSION_403_DETAILS = new Set([
  'Access denied',
  'Read-only access',
  'Not the owner',
  'Owner only',
  'Not a member',
  'Not allowed',
]);

async function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 25000): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

class ApiClient {
  private async requestOnce<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('colibri_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetchWithTimeout(`${API_BASE}/${path}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      localStorage.removeItem('colibri_token');
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      let detail = `API error: ${response.status}`;
      try {
        const body = await response.json();
        if (body?.detail) detail = String(body.detail);
      } catch {
        /* ignore */
      }
      const isWrite = (options.method || 'GET') !== 'GET';
      if (response.status === 403 && isWrite && PERMISSION_403_DETAILS.has(detail)) {
        notifyPermissionDenied();
      }
      throw new Error(detail);
    }

    return response.json();
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const isGet = (options.method || 'GET') === 'GET';
    const attempts = isGet ? 3 : 1;
    for (let i = 0; i < attempts; i++) {
      try {
        return await this.requestOnce<T>(path, options);
      } catch (e) {
        const msg = String((e as Error)?.message || e);
        const networkGlitch = /Failed to fetch|Abort|timeout|network|502|503|504/i.test(msg);
        if (!networkGlitch || i === attempts - 1) throw e;
        await new Promise((resolve) => setTimeout(resolve, 700 * (i + 1)));
      }
    }
    throw new Error('Network error');
  }

  get<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'GET' });
  }

  post<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  put<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const api = new ApiClient();
